import { type KeyboardEvent, type ReactElement, useState } from 'react';
import { parseOrderQty } from '@/entities/product';
import { type SizeFieldValues, SizeTotalFields, sizeTotalsFromFields } from '@/entities/quote';
import {
  ColorSelect,
  EditorOrderSummary,
  EditorQuantityField,
  LayerList,
  layersOf,
  reorderNode,
  SaveErrorNotice,
} from '@/features/design-editor';
import { ONBOARDING_PATH, type SizeQuantities } from '@/shared/config';
import { formatCount, formatKrw } from '@/shared/lib';
import { EyebrowLabel } from '@/shared/ui';
import type { CustomizeEditorReady } from '../model/use-customize-editor';
import { SelectedLayerSection } from './selected-layer-section';

interface EditorPanelProps {
  readonly editor: CustomizeEditorReady;
  /** 상세에서 고른 사이즈별 수량 — 이 상품 사이즈만 처음 값으로 쓴다. */
  readonly initialSizes?: SizeQuantities;
}

const saveStatusOf = (editor: CustomizeEditorReady): string => {
  // 비회원은 저장 없이 견적을 요청한다(G5) — 임시 작업은 [견적 요청하기] 때 이 기기에 남긴다.
  if (!editor.canSave) return '로그인하지 않아도 지금 디자인 그대로 견적을 요청할 수 있습니다.';
  if (editor.saver.isSaving) return '저장하는 중…';
  if (editor.store.designId === null) return '아직 저장하지 않았습니다.';
  return editor.store.dirty ? '저장되지 않은 변경이 있습니다.' : '저장했습니다.';
};

/**
 * 오른쪽 주문 패널 — 상품 상세 오른쪽 패널과 같은 결. 카드 없이 스테이지와 얇은 세로선으로 나누고,
 * 머리말(CUSTOMIZE)·상품명·시작 가격, 색상 견본, 수량(수량별 가격), 선택한 레이어 편집, 레이어 줄 목록을
 * 얇은 가로선으로 나눈다. 레이어를 고르면 이 자리가 그 레이어의 스타일 패널([←]·Esc 로 돌아온다)로 바뀐다.
 * 아래에는 예상 합계와 [저장](회원만)·[견적 요청하기]가 늘 붙는다.
 * 수량은 예상 합계용 화면 상태이며, 견적 요청 화면의 처음 수량으로 이어진다.
 */
export const EditorPanel = ({ editor, initialSizes = {} }: EditorPanelProps): ReactElement => {
  const { product, store, saver, component, actions } = editor;
  const [qtyDraftState, setQtyDraft] = useState(String(product.minOrderQty));
  const hasSizes = product.sizeOptions.length > 0;
  // 사이즈 상품은 사이즈별 수량의 합계가 수량이다 — 상세에서 넘어온 값으로 시작한다.
  const [sizeDrafts, setSizeDrafts] = useState<SizeFieldValues>(() =>
    Object.fromEntries(
      product.sizeOptions.flatMap((option) => {
        const quantity = initialSizes[option.id];
        return quantity === undefined ? [] : [[option.id, String(quantity)]];
      }),
    ),
  );
  const sizeTotals = hasSizes ? sizeTotalsFromFields(product.sizeOptions, sizeDrafts) : null;
  const sizeQuantities =
    sizeTotals === null
      ? null
      : Object.fromEntries(
          sizeTotals.bySize
            .filter((row) => row.quantity > 0)
            .map((row) => [row.sizeOptionId, row.quantity]),
        );
  const qtyDraft = hasSizes ? (sizeTotals === null ? '' : String(sizeTotals.total)) : qtyDraftState;
  const qty = parseOrderQty(qtyDraft);
  const validQty = qty !== null && qty >= product.minOrderQty ? qty : null;
  const { lastError } = saver;
  // 상품 상세와 같은 시작 가격(기본 단가) — 없으면 수량 구간 중 가장 싼 개당 단가.
  const fromPrice =
    product.baseUnitPrice ??
    (product.priceTiers.length > 0
      ? Math.min(...product.priceTiers.map((tier) => tier.unitPrice))
      : null);
  const layers = component === null ? [] : layersOf(store.document, component.id);
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || store.selectedNodeId === null) return;
    event.preventDefault();
    store.clearSelection();
  };

  return (
    <aside
      aria-label="주문 정보"
      className="flex flex-col border-foreground/10 lg:min-h-0 lg:border-l lg:pl-8 xl:pl-10"
      onKeyDown={onKeyDown}
    >
      <div className="flex-1 space-y-7 lg:min-h-0 lg:overflow-y-auto lg:pr-1 lg:[scrollbar-width:thin]">
        {editor.selected !== null ? (
          <SelectedLayerSection editor={editor} />
        ) : (
          <>
            <div className="space-y-3">
              <EyebrowLabel accent>Customize</EyebrowLabel>
              <h1 className="text-[clamp(1.5rem,2vw,2rem)] leading-[1.2] font-bold tracking-[-0.02em] text-balance">
                {product.name}
              </h1>
              {fromPrice !== null ? (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-xl font-bold tracking-[-0.01em] tabular-nums">
                    {formatKrw(fromPrice)}부터
                  </p>
                  <span className="bg-brand-lime px-1.5 py-0.5 text-xs font-semibold text-black tabular-nums">
                    최소 {formatCount(product.minOrderQty)}개
                  </span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  가격은 견적 상담에서 안내해 드립니다.
                </p>
              )}
              {component !== null && (
                <p className="text-sm text-muted-foreground tabular-nums">
                  {component.name} · {component.widthMm}×{component.heightMm}mm
                </p>
              )}
            </div>

            {editor.restoredDraft && (
              <p
                role="status"
                className="border-l-2 border-brand-lime bg-muted/70 px-3 py-2 text-xs font-medium"
              >
                저장하지 않은 편집을 불러왔습니다
              </p>
            )}

            <div className="space-y-7 border-t border-foreground/10 pt-6">
              <ColorSelect
                colors={editor.colors}
                value={store.document.colorOptionId}
                onChange={store.setColor}
              />
              {hasSizes ? (
                <div className="space-y-2">
                  <SizeTotalFields
                    options={product.sizeOptions}
                    values={sizeDrafts}
                    onChange={(sizeOptionId, value) =>
                      setSizeDrafts((previous) => ({ ...previous, [sizeOptionId]: value }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    합계 {sizeTotals === null ? '—' : `${formatCount(sizeTotals.total)}개`} · 최소{' '}
                    {formatCount(product.minOrderQty)}개부터 주문할 수 있습니다.
                  </p>
                </div>
              ) : (
                <EditorQuantityField
                  value={qtyDraft}
                  minOrderQty={product.minOrderQty}
                  tiers={product.priceTiers}
                  onChange={setQtyDraft}
                />
              )}
            </div>

            {component !== null && (
              <section
                aria-label="레이어 목록"
                className="space-y-3 border-t border-foreground/10 pt-6"
              >
                <div className="space-y-2">
                  <EyebrowLabel>Layers</EyebrowLabel>
                  <h2 className="text-base font-bold">
                    레이어{' '}
                    <span className="font-normal text-muted-foreground tabular-nums">
                      {layers.length}
                    </span>
                  </h2>
                </div>
                <LayerList
                  layers={layers}
                  selectedNodeId={store.selectedNodeId}
                  invalidNodeIds={saver.invalidNodeIds}
                  onSelect={store.select}
                  onReorder={(id, direction) =>
                    store.mutate((doc) => reorderNode(doc, id, direction))
                  }
                />
              </section>
            )}
          </>
        )}
      </div>

      {/* 좁은 화면에서는 화면 아래에 붙어 따라온다 — 본문 여백만큼 넓혀 전폭으로 깐다. */}
      <div className="sticky bottom-0 z-10 -mx-5 mt-6 bg-background px-5 shadow-[0_-12px_24px_-20px_rgb(0_0_0/0.35)] sm:-mx-8 sm:px-8 lg:static lg:mx-0 lg:mt-0 lg:px-0 lg:shadow-none">
        <EditorOrderSummary
          qty={validQty}
          minOrderQty={product.minOrderQty}
          tiers={product.priceTiers}
          saveStatus={saveStatusOf(editor)}
          notice={
            lastError === null ? null : (
              <SaveErrorNotice
                message={lastError.message}
                onRetry={lastError.kind === 'conflict' ? actions.save : null}
                onboardingHref={lastError.kind === 'identity' ? ONBOARDING_PATH : null}
              />
            )
          }
          busy={saver.isSaving}
          onSave={editor.canSave ? actions.save : null}
          onRequestQuote={() => actions.requestQuote(validQty, sizeQuantities)}
        />
      </div>
    </aside>
  );
};
